import { useEffect, useMemo, useRef, useState } from "react";
import { useProactiveIdentity } from "@/hooks/useProactiveIdentity";
import { emailSessionRequest, type EmailSessionGuard } from "@/lib/emailSessionRequest";
import { EMAIL_OPERATIONS, emailThreadPage, type EmailThreadPage, emailSearchPage, emailMessageDetail, emailAttachmentContent, emailAttachmentReadable, type EmailProvider, type EmailSummary, type EmailDetail, type EmailAttachment, type EmailOperation, type EmailOrganizationSnapshot } from "@/lib/emailReader";
import { emailReaderCopy } from "@/lib/emailReaderCopy";
import { deliverBase64File } from "@/lib/nativeFileDelivery";
import { useConnectorWrite } from "@/hooks/useConnectorWrite";
import { emitCaptureRecap } from "@/lib/capture/captureRecapBus";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, Check, ChevronLeft, FilePlus2, ListTodo, Loader2, Mail, Search, Send, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useEmailConnectionAccess } from "@/hooks/useEmailConnectionAccess";
import { useConnections } from "@/hooks/useConnections";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";
import { toast } from "sonner";
import ConnectionManageSurface from "@/components/connections/ConnectionManageSurface";
import { ConnectionBrandMark } from "@/components/connections/ConnectionBrandMark";
import { mirrorEmailReceiptReference, type MirrorEmailReceiptReference } from "@/lib/mirrorEmailReceipt";
import { agendaOwnerSession } from "@/lib/agendaExecutor";
import { createAgendaContextHandoff } from "@/lib/agendaContextHandoff";
import { createConfirmedTask, taskClientKey, withTaskOwner } from "@/lib/taskMutations";

export type { EmailProvider } from "@/lib/emailReader";
export type EmailActionKind = "search" | "draft" | "send" | "reply" | "organize";

export interface EmailActionPrefill {
  action: EmailActionKind;
  provider: "auto" | EmailProvider;
  query: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body_text: string;
  action_id: string;
  expected_user_id?: string;
  expected_session_id?: string;
  message_id?: string;
  operation?: EmailOperation;
}

interface Props {
  open: boolean;
  prefill: EmailActionPrefill | null;
  onClose: () => void;
  onCreated: (result?: MirrorEmailReceiptReference) => void;
}

const csv = (value: string): string[] => value.split(",").map((v) => v.trim()).filter(Boolean);

export function EmailActionModal(props: Props) {
  const { gmailEnabled, outlookEnabled, identityKey } = useEmailConnectionAccess();
  const identity = useProactiveIdentity();
  if (!props.open || !props.prefill || !identity.ready || !identity.guard || identity.userId !== identityKey) return null;
  if ((props.prefill.expected_user_id || props.prefill.expected_session_id) &&
    (props.prefill.expected_user_id !== identityKey || !props.prefill.expected_session_id)) return null;
  return <EmailActionContent key={[identityKey, identity.guard.authSessionId, identity.generation, props.prefill.expected_session_id, props.prefill.action_id, props.prefill.provider, props.prefill.action].join(":")} {...props} ownerId={identityKey} sessionGuard={identity.guard} gmailEnabled={gmailEnabled} outlookEnabled={outlookEnabled} />;
}

function EmailActionContent({ open, prefill, onClose, onCreated, gmailEnabled, outlookEnabled, ownerId, sessionGuard }: Props & { gmailEnabled: boolean; outlookEnabled: boolean; ownerId: string; sessionGuard: EmailSessionGuard }) {
  const { t, lang } = useLanguage();
  const readerCopy = emailReaderCopy(lang);
  const navigate = useNavigate();
  const surfaceQuota = useQuotaErrorSurface();
  const requestEpoch = useRef(0);
  const current = () => sessionGuard.isCurrent();
  const captureCurrent = () => { const epoch = requestEpoch.current; return () => sessionGuard.isCurrent() && epoch === requestEpoch.current; };
  const write = useConnectorWrite(ownerId, "email", surfaceQuota, { captureCurrent, authSessionId: sessionGuard.authSessionId });
  const { gmailConnection, outlookMailConnection } = useConnections({ counts: false });
  const [provider, setProvider] = useState<EmailProvider>("gmail");
  const [mode, setMode] = useState<EmailActionKind>("search");
  const [query, setQuery] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [attachments, setAttachments] = useState<Array<{ filename: string; mime_type: string; base64: string; bytes: number }>>([]);
  const [results, setResults] = useState<EmailSummary[]>([]);
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const searchPages = useRef<{ provider: EmailProvider; query: string; cursors: Set<string>; pages: number } | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{ id: string; text: string; truncated: boolean } | null>(null);
  const [organizeReview, setOrganizeReview] = useState<{ operation: EmailOperation; actionId: string; snapshot: EmailOrganizationSnapshot; mirror: boolean } | null>(null);
  const [openingMessageId, setOpeningMessageId] = useState<string | null>(null);
  const readerHeadingRef = useRef<HTMLHeadingElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const openButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const lastOpenedMessageRef = useRef<string | null>(null);
  const [selected, setSelected] = useState<EmailDetail | null>(null);
  const [readBusy, setBusy] = useState(false);
  const [encoding, setEncoding] = useState(false);
  const attachmentLock = useRef(false);
  const readLock = useRef(false);
  const [thread, setThread] = useState<EmailThreadPage | null>(null);
  const threadPages = useRef({pages:0,cursors:new Set<string>()});
  const authOwner = useRef(ownerId);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? "";
      if (next !== authOwner.current) { requestEpoch.current++; setSelected(null); setResults([]); }
      authOwner.current = next;
    });
    const lifecycle = requestEpoch;
    return () => { subscription.unsubscribe(); lifecycle.current++; };
  }, []);
  const directMessageRef = useRef<string | null>(null);
  const openMessageRef = useRef<(message: EmailSummary) => Promise<boolean>>(async () => false);
  const busy = readBusy || write.busy || encoding;
  const [error, setError] = useState<string | null>(null);
  const done = write.pending?.status === "succeeded";
  const writeUncertain = !!write.pending && !done;

  const gmailActive = gmailEnabled && gmailConnection?.status === "active";
  const outlookActive = outlookEnabled && outlookMailConnection?.status === "active";
  const availableProviders = useMemo<EmailProvider[]>(() => [
    ...(gmailActive ? ["gmail" as const] : []),
    ...(outlookActive ? ["outlook_mail" as const] : []),
  ], [gmailActive, outlookActive]);

  useEffect(() => {
    if (!open || !prefill) return;
    const desired = prefill.provider !== "auto" ? prefill.provider : availableProviders[0] ?? "gmail";
    setProvider(desired);
    setMode(prefill.action);
    setQuery(prefill.query || "");
    setTo((prefill.to || []).join(", "));
    setCc((prefill.cc || []).join(", "));
    setBcc((prefill.bcc || []).join(", "));
    setSubject(prefill.subject || "");
    setBodyText(prefill.body_text || "");
    setAttachments([]);
    setResults([]);
    setSearchedQuery(null); setNextCursor(null); searchPages.current = null;
    setAttachmentPreview(null); setOrganizeReview(null); setThread(null); threadPages.current={pages:0,cursors:new Set()};
    setOpeningMessageId(null);
    lastOpenedMessageRef.current = null;
    setSelected(null);
    setBusy(false);
    readLock.current = false;
    directMessageRef.current = null;
    setError(null);
    const lifecycle = requestEpoch;
    return () => { lifecycle.current++; };
  }, [open, prefill, availableProviders]);

  useEffect(() => {
    if (!write.pending) return;
    const body = write.pending.body;
    setProvider(body.provider as EmailProvider); setMode(body.action as EmailActionKind);
    setTo(((body.to as string[]) || []).join(", "));
    setCc(((body.cc as string[]) || []).join(", ")); setBcc(((body.bcc as string[]) || []).join(", "));
    setSubject(String(body.subject || "")); setBodyText(String(body.body_text || ""));
    setAttachments(((body.attachments as Array<{ filename: string; mime_type: string; base64: string }>) || []).map(item => ({ ...item, bytes: Math.floor(item.base64.length * 3 / 4) })));
    if (body.message_id) setSelected({ id: String(body.message_id), subject: write.pending.preview?.title, from: write.pending.preview?.subtitle });
    if (body.action === "organize") setOrganizeReview({ operation: body.operation as EmailOperation,
      actionId: "", snapshot: body.expected_snapshot as EmailOrganizationSnapshot, mirror: !!body.mirror_context });
  }, [write.pending, availableProviders]);

  useEffect(() => {
    if (!open) return;
    if (selected && (mode === "search" || mode === "organize")) {
      readerHeadingRef.current?.focus();
    } else if (selected && mode === "reply") {
      replyInputRef.current?.focus();
    } else if (lastOpenedMessageRef.current) {
      const button = openButtonRefs.current.get(lastOpenedMessageRef.current);
      button?.focus();
      if (button) lastOpenedMessageRef.current = null;
    }
  }, [open, selected, mode]);

  useEffect(() => {
    // Only a provider-specific, observed message may preselect the reply.
    // This reads the original for review and never submits a write.
    if (!open || !prefill || !availableProviders.includes(provider) || !write.ready || write.pending ||
      !["reply", "organize"].includes(prefill.action) || prefill.provider === "auto" || prefill.provider !== provider || !prefill.message_id) return;
    const key = provider + ":" + prefill.message_id;
    if (directMessageRef.current === key) return;
    directMessageRef.current = key;
    void openMessageRef.current({ id: prefill.message_id });
  }, [open, availableProviders, write.ready, write.pending, prefill, provider]);

  if (!prefill) return null;

  const connected = availableProviders.includes(provider);
  const providerLabel = provider === "gmail" ? "Gmail" : "Outlook Mail";
  const reading = mode === "search" && selected !== null;
  const title = mode === "organize" ? readerCopy.organize : reading ? t("mirror.email.read.title") : t(`mirror.email.${mode}.title`);
  const description = mode === "organize" ? readerCopy.review : reading ? providerLabel : t(`mirror.email.${mode}.description`);

  const addAttachments = async (files: FileList | null) => {
    if (!files?.length || busy || attachmentLock.current || write.pending) return;
    attachmentLock.current = true; setEncoding(true);
    const epoch = requestEpoch.current;
    setError(null);
    const next = [...attachments];
    let totalBytes = next.reduce((sum, item) => sum + item.bytes, 0);
    try {
    if (files.length + next.length > 5) { setError(t("mirror.email.attachmentTooLarge")); return; }
    for (const file of Array.from(files)) {
      if (file.size > 3 * 1024 * 1024 || totalBytes + file.size > 5 * 1024 * 1024) { setError(t("mirror.email.attachmentTooLarge")); continue; }
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (epoch !== requestEpoch.current || !current()) return;
      let binary = "";
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      next.push({ filename: file.name, mime_type: file.type || "application/octet-stream", base64: btoa(binary), bytes: file.size });
      totalBytes += file.size;
    }
    setAttachments(next);
    } catch { if (epoch === requestEpoch.current && current()) setError(t("mirror.email.error")); }
    finally { attachmentLock.current = false; if (epoch === requestEpoch.current && current()) setEncoding(false); }
  };

  const invoke = (body: Record<string, unknown>) => {
    const isCurrent = captureCurrent();
    return emailSessionRequest(ownerId, body, { authSessionId: sessionGuard.authSessionId, isCurrent }, surfaceQuota);
  };
  const quotaWasSurfaced = (cause: unknown) => cause instanceof Error && cause.message === "__quota_surfaced__";

  const searchMailbox = async (more = false) => {
    if (!connected || busy || readLock.current || !current()) return;
    const previous = searchPages.current;
    if (more && (!nextCursor || !previous || previous.provider !== provider || previous.query !== query || previous.pages >= 20 || previous.cursors.has(nextCursor))) return;
    const requestedCursor = more ? nextCursor : null;
    readLock.current = true;
    const epoch = requestEpoch.current;
    setBusy(true); setError(null); setSelected(null); setAttachmentPreview(null); setOrganizeReview(null);
    if (!more) { setResults([]); setSearchedQuery(null); setNextCursor(null); searchPages.current = null; }
    try {
      const data = await invoke({ provider, action: "search", query, limit: 12, ...(requestedCursor ? { cursor: requestedCursor } : {}) });
      const page = emailSearchPage(data, provider);
      if (epoch !== requestEpoch.current || !current()) return;
      const cursors = new Set(more ? previous!.cursors : []);
      if (requestedCursor) cursors.add(requestedCursor);
      if (page.nextCursor && (cursors.has(page.nextCursor) || page.nextCursor === requestedCursor)) throw new Error("email_cursor_cycle");
      const pages = more ? previous!.pages + 1 : 1;
      searchPages.current = { provider, query, cursors, pages };
      setResults(old => [...new Map([...(more ? old : []), ...page.messages].map(item => [item.id, item])).values()]);
      setNextCursor(pages < 20 ? page.nextCursor : null);
      setSearchedQuery(query);
    } catch (cause) {
      if (epoch !== requestEpoch.current || !current()) return;
      if (cause instanceof Error && cause.message === "email_cursor_cycle") setNextCursor(null);
      if (!quotaWasSurfaced(cause)) setError(t("mirror.email.error"));
    } finally { if (epoch === requestEpoch.current && current()) { readLock.current = false; setBusy(false); } }
  };

  const openMessage = async (message: EmailSummary) => {
    if (!connected || busy || readLock.current || !current()) return false;
    readLock.current = true;
    const epoch = requestEpoch.current;
    setBusy(true); setError(null); setOpeningMessageId(message.id);
    try {
      const data = await invoke({ provider, action: "get", message_id: message.id });
      if (data?.provider !== undefined && data.provider !== provider) throw new Error("email_read_unconfirmed");
      const detail = emailMessageDetail(data?.message, provider, message.id);
      if (epoch !== requestEpoch.current || !current()) return false;
      lastOpenedMessageRef.current = message.id;
      setSelected(detail); setAttachmentPreview(null); setOrganizeReview(null); setThread(null); threadPages.current={pages:0,cursors:new Set()};
      if (prefill.action === "organize" && prefill.operation && detail.organization_snapshot && EMAIL_OPERATIONS.includes(prefill.operation)) {
        setOrganizeReview({ operation: prefill.operation, snapshot: detail.organization_snapshot, actionId: prefill.action_id, mirror: true });
      }
      return true;
    } catch (cause) {
      if (epoch !== requestEpoch.current || !current()) return false;
      console.warn("[EmailActionModal] get failed", cause);
      if (!quotaWasSurfaced(cause)) setError(t("mirror.email.error"));
      return false;
    } finally { if (epoch === requestEpoch.current && current()) { readLock.current = false; setBusy(false); setOpeningMessageId(null); } }
  };

  const chooseReply = async (message: EmailSummary) => {
    if (await openMessage(message)) setMode("reply");
  };
  openMessageRef.current = openMessage;

  const readConversation = async (more = false) => {
    if (!selected || busy || readLock.current || !current()) return;
    const previous=thread,root=more?previous?.root:selected.id,cursor=more?previous?.nextCursor:null;
    if (!root || more && (!cursor || threadPages.current.pages >= 20 || threadPages.current.cursors.has(cursor!))) return;
    readLock.current=true;const epoch=requestEpoch.current;setBusy(true);setError(null);
    try {
      const response=await invoke({provider,action:"thread",message_id:root,limit:5,...(cursor?{cursor}:{})});
      const page=emailThreadPage(response,provider,root);
      if(epoch!==requestEpoch.current||!current())return;
      const cursors=new Set(more?threadPages.current.cursors:[]);
      if(cursor)cursors.add(cursor);
      if(page.nextCursor&&cursors.has(page.nextCursor))throw Error("email_cursor_cycle");
      if(more&&previous&&page.conversation!==previous.conversation)throw Error("email_read_unconfirmed");
      threadPages.current={pages:more?threadPages.current.pages+1:1,cursors};
      setThread({...page,messages:[...new Map([...(more?previous!.messages:[]),...page.messages].map(message=>[message.id,message])).values()]});
    } catch(cause){if(epoch===requestEpoch.current&&current()&&!quotaWasSurfaced(cause))setError(t("mirror.email.error"));}
    finally{if(epoch===requestEpoch.current&&current()){readLock.current=false;setBusy(false);}}
  };

  const reviewOrganization = (operation: EmailOperation) => {
    if (!selected?.organization_snapshot || busy || write.pending || !current()) return;
    setOrganizeReview({ operation, actionId: crypto.randomUUID(), snapshot: selected.organization_snapshot, mirror: false });
    setAttachmentPreview(null); setMode("organize"); setError(null);
  };

  const readAttachment = async (attachment: EmailAttachment) => {
    if (!selected || busy || readLock.current || attachment.bytes > 3 * 1024 * 1024 || !current()) return;
    readLock.current = true; const epoch = requestEpoch.current; const messageId = selected.id;
    setBusy(true); setError(null); setAttachmentPreview(null);
    try {
      const response = await invoke({ provider, action: "attachment", message_id: messageId, attachment_id: attachment.id, format: "text" });
      const content = emailAttachmentContent(response, provider, messageId, attachment, "text");
      if (epoch === requestEpoch.current && current()) setAttachmentPreview({ id: attachment.id, text: content.content_text!, truncated: content.truncated! });
    } catch (cause) {
      if (epoch === requestEpoch.current && current() && !quotaWasSurfaced(cause)) setError(t("mirror.email.error"));
    } finally { if (epoch === requestEpoch.current && current()) { readLock.current = false; setBusy(false); } }
  };

  const downloadAttachment = async (attachment: EmailAttachment) => {
    if (!selected || busy || readLock.current || attachment.bytes > 3 * 1024 * 1024 || !current()) return;
    const isCurrent = captureCurrent(); const messageId = selected.id;
    readLock.current = true; setBusy(true); setError(null);
    try {
      const response = await invoke({ provider, action: "attachment", message_id: messageId, attachment_id: attachment.id, format: "download" });
      const content = emailAttachmentContent(response, provider, messageId, attachment, "download");
      if (!isCurrent()) return;
      await deliverBase64File({ base64: content.base64!, fileName: content.filename, mimeType: content.mime_type.split(";")[0].trim(), isCurrent });
    } catch (cause) { if (isCurrent() && !quotaWasSurfaced(cause)) setError(t("mirror.email.error")); }
    finally { if (isCurrent()) { readLock.current = false; setBusy(false); } }
  };

  const executeWrite = async () => {
    if ((!connected && !write.pending) || busy || attachmentLock.current || !current()) return;
    if (!write.pending && mode !== "reply" && mode !== "organize" && csv(to).length === 0) { setError(t("mirror.email.recipientRequired")); return; }
    if (!write.pending && mode === "reply" && !selected?.id) { setError(t("mirror.email.selectMessage")); return; }
    if (!write.pending && mode === "organize" && (!selected?.id || !organizeReview)) return;
    setError(null);
    const epoch = requestEpoch.current;
    const actionId = mode === "organize" ? organizeReview!.actionId : prefill.action_id;
    const mirror = mode !== "organize" || organizeReview?.mirror;
    const idempotencyKey = `mirror_email_${actionId}_${mode}_0`;
    const completed = await write.execute(() => ({
      provider, action: mode, idempotency_key: idempotencyKey, auth_session_id: sessionGuard.authSessionId,
      ...(mode === "organize" ? { message_id: selected!.id, operation: organizeReview!.operation, expected_snapshot: organizeReview!.snapshot, confirmed: true }
        : { ...(mode === "reply" ? { message_id: selected!.id } : { to: csv(to), cc: csv(cc), bcc: csv(bcc), subject }),
          body_text: bodyText, attachments: attachments.map(({ filename, mime_type, base64 }) => ({ filename, mime_type, base64 })) }),
      ...(mirror && prefill.expected_user_id === ownerId && prefill.expected_session_id ? {
        mirror_context: { session_id: prefill.expected_session_id, action_id: prefill.action_id },
      } : {}),
    }), { title: selected?.subject, subtitle: selected?.from });
    if (completed && epoch === requestEpoch.current && current()) {
      const reference = prefill.expected_user_id && prefill.expected_session_id
        ? mirrorEmailReceiptReference(completed, { userId: prefill.expected_user_id,
          sessionId: prefill.expected_session_id, actionId: prefill.action_id }) : null;
      if (!prefill.expected_user_id || reference) {
        emitCaptureRecap({ kind: "email", title: completed.body.action === "organize" ? readerCopy.done : t(`mirror.email.${completed.body.action}.done`), subtitle: completed.body.provider === "gmail" ? "Gmail" : "Outlook Mail" });
        onCreated(reference ?? undefined);
      }
    }
  };

  const createTaskFromEmail = async () => {
    if (!selected?.id || busy || !current()) return;
    const epoch = requestEpoch.current;
    setBusy(true); setError(null);
    try {
      await withTaskOwner(ownerId, () => { if (epoch !== requestEpoch.current || !current()) throw new Error("TASK_SESSION_UNAVAILABLE"); }, async (uid, assert) => {
        const key = await taskClientKey(uid, `email_task_${provider}_${selected.id}`, `email_task_${provider}`); assert();
        const title = [selected.subject || t("mirror.email.noSubject"), selected.from ? `${t("mirror.email.from")}: ${selected.from}` : ""].filter(Boolean).join("\n\n");
        await createConfirmedTask({ client_action_id: key, source: "mirror", title, priority: "Normal", due_date: null, time_label: null }, uid, assert);
        assert();
      });
      if (epoch === requestEpoch.current && current()) toast.success(t("mirror.email.taskCreated"));
    } catch (cause) {
      console.warn("[EmailActionModal] create task failed", cause);
      if (epoch === requestEpoch.current && current()) toast.error(t("mirror.email.taskError"));
    } finally { if (epoch === requestEpoch.current && current()) setBusy(false); }
  };

  const addEmailToCalendar = async () => {
    if (!selected?.id || authOwner.current !== ownerId || !current()) return;
    const epoch = requestEpoch.current;
    const input = { title: selected.subject || t("mirror.email.noSubject"),
      notes: [selected.from ? `${t("mirror.email.from")}: ${selected.from}` : "", selected.snippet || ""].filter(Boolean).join("\n\n") || null };
    const assert = () => { if (epoch !== requestEpoch.current || authOwner.current !== ownerId || !current()) throw new Error("invalid_session"); };
    try {
      assert(); await agendaOwnerSession(ownerId, assert); assert();
      const handoff = createAgendaContextHandoff(input, ownerId);
      if (!handoff) throw new Error("invalid_agenda_context");
      assert(); onClose(); navigate("/agenda", { state: { vyvAgendaContext: handoff } });
    } catch { if (epoch === requestEpoch.current && current()) setError(t("mirror.connector.accountChanged")); }
  };

  const goConnect = () => {
    onClose();
    navigate("/settings/connections?group=messages");
  };

  const close = async () => { if (!done || await write.acknowledge()) onClose(); };
  const writeError = write.error === "pending_storage_unavailable" ? t("mirror.connector.storageUnavailable")
    : write.error === "invalid_session" ? t("mirror.connector.accountChanged")
    : write.error === "__quota_surfaced__" ? null
    : writeUncertain && !busy ? t("mirror.connector.outcomeUnknown")
    : write.error ? t("mirror.email.error") : null;
  const visibleError = error || writeError;

  const brandProvider = availableProviders.includes(provider) ? provider : (prefill.provider === "outlook_mail" ? "outlook_mail" : "gmail");

  return (
    <ConnectionManageSurface
      open={open}
      onOpenChange={(next) => { if (!next && !busy) void close(); }}
      title={title}
      description={description}
      closeLabel={t("common.close")}
      brand={<ConnectionBrandMark provider={brandProvider} size="lg" />}
    >
      {!connected && !write.pending ? (
        <div className="space-y-4 pt-2">
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
            <p className="text-sm font-medium text-foreground">{t("mirror.email.notConnected")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("mirror.email.notConnectedHint")}</p>
          </div>
          <button type="button" onClick={goConnect} className="min-h-12 w-full rounded-xl bg-primary px-4 font-semibold text-primary-foreground">
            {t("mirror.email.openConnections")}
          </button>
        </div>
      ) : done ? (
        <div className="space-y-4 py-3 text-center">
          {writeError && <p role="alert" className="text-sm text-destructive">{writeError}</p>}
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500"><Check className="h-7 w-7" /></span>
          <div><p className="font-semibold text-foreground">{mode === "organize" ? readerCopy.done : t(`mirror.email.${mode}.done`)}</p><p className="mt-1 text-sm text-muted-foreground">{providerLabel}</p></div>
          <button type="button" onClick={() => void close()} className="min-h-11 w-full rounded-xl border border-border px-4 font-medium">{t("common.close")}</button>
        </div>
      ) : (
        <div className="min-w-0 space-y-4 pt-1">
          <fieldset disabled={busy || writeUncertain} className="min-w-0 space-y-4">
          {availableProviders.length > 1 && (
            <div className="grid grid-cols-2 gap-2">
              {availableProviders.map((item) => (
                <button key={item} type="button" onClick={() => { setProvider(item); setThread(null); threadPages.current={pages:0,cursors:new Set()}; setResults([]); setSearchedQuery(null); setNextCursor(null); searchPages.current = null; setSelected(null); setAttachmentPreview(null); setOrganizeReview(null); setError(null); }} className={`min-h-11 rounded-xl border px-3 text-sm font-semibold ${provider === item ? "border-primary/50 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  {item === "gmail" ? "Gmail" : "Outlook Mail"}
                </button>
              ))}
            </div>
          )}

          {!selected && (mode === "search" || mode === "reply") && (
            <>
              <label className="block space-y-1.5"><span className="text-xs font-medium text-muted-foreground">{t("mirror.email.searchLabel")}</span><div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><input value={query} onChange={(e) => setQuery(e.target.value)} className="min-h-12 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-sm" placeholder={t("mirror.email.searchPlaceholder")} /></div></label>
              <button type="button" onClick={() => void searchMailbox()} disabled={busy} className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}{t("mirror.email.search")}</button>
              <div className="space-y-2">
                {results.map((message) => (
                  <div key={message.id} className="min-w-0 rounded-xl border border-border bg-card p-3 [overflow-wrap:anywhere]">
                    <p className="line-clamp-2 text-sm font-semibold text-foreground">{message.subject || t("mirror.email.noSubject")}</p>
                    <p className="mt-0.5 break-words text-xs text-muted-foreground">{message.from_name || message.from || ""}</p>
                    {message.snippet && <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{message.snippet}</p>}
                    <div className="mt-3 flex gap-2"><button type="button" ref={(node) => { if (node) openButtonRefs.current.set(message.id, node); else openButtonRefs.current.delete(message.id); }} onClick={() => void openMessage(message)} aria-busy={openingMessageId === message.id} className="inline-flex min-h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-lg border border-border px-3 text-sm font-medium">{openingMessageId === message.id && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" />}{t("mirror.email.open")}</button>{prefill.action === "reply" && <button type="button" onClick={() => void chooseReply(message)} className="min-h-10 flex-1 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground">{t("mirror.email.reply")}</button>}</div>
                  </div>
                ))}
                {nextCursor && searchedQuery === query && <button type="button" onClick={() => void searchMailbox(true)} disabled={busy} className="min-h-11 w-full rounded-xl border border-border px-3 text-sm font-medium">{readerCopy.more}</button>}
                {!busy && !error && searchedQuery === query && results.length === 0 && <p className="py-3 text-center text-sm text-muted-foreground">{t("mirror.email.noResults")}</p>}
              </div>
            </>
          )}

          {selected && mode === "search" && (
            <div className="min-w-0 space-y-4 [overflow-wrap:anywhere]">
              <button type="button" onClick={() => setSelected(null)} className="inline-flex min-h-10 items-center gap-1 text-sm font-medium text-primary"><ChevronLeft className="h-4 w-4" />{t("common.back")}</button>
              <div><h3 ref={readerHeadingRef} tabIndex={-1} className="text-base font-semibold text-foreground">{selected.subject || t("mirror.email.noSubject")}</h3><p className="mt-1 text-xs text-muted-foreground">{selected.from_name || selected.from || ""}</p></div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <button type="button" onClick={() => setMode("reply")} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-3 text-sm font-semibold text-primary"><Mail className="h-4 w-4" />{t("mirror.email.reply")}</button>
                <button type="button" onClick={() => void createTaskFromEmail()} disabled={busy} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border px-3 text-sm font-semibold text-foreground disabled:opacity-50"><ListTodo className="h-4 w-4" />{t("mirror.email.createTask")}</button>
                <button type="button" onClick={addEmailToCalendar} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border px-3 text-sm font-semibold text-foreground"><CalendarPlus className="h-4 w-4" />{t("mirror.email.addToCalendar")}</button>
              </div>
              <button type="button" onClick={() => void readConversation()} disabled={busy} className="min-h-11 w-full rounded-xl border border-border px-3 text-sm font-medium">{readerCopy.threadRead}</button>
              {thread && <section className="space-y-3 rounded-xl border border-border p-3" aria-label={readerCopy.threadTitle}>
                <h4 className="font-semibold">{readerCopy.threadTitle}</h4>
                {thread.messages.map(message => <article key={message.id} className="border-t border-border pt-3">
                  <p className="text-sm font-semibold">{message.subject || t("mirror.email.noSubject")}</p>
                  <p className="text-xs text-muted-foreground">{message.from_name || message.from || ""} {message.date || ""}</p>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm">{message.body_text || ""}</p>
                  <button type="button" onClick={() => void openMessage(message)} className="mt-2 min-h-11 rounded-lg border border-border px-3 text-sm">{t("mirror.email.open")}</button>
                </article>)}
                <p className="text-xs text-muted-foreground">{thread.hasMore ? readerCopy.threadMore : readerCopy.threadComplete}</p>
                {thread.truncated && <p className="text-xs text-muted-foreground">{readerCopy.truncated}</p>}
                {thread.nextCursor && threadPages.current.pages<20 && <button type="button" onClick={() => void readConversation(true)} disabled={busy} className="min-h-11 w-full rounded-lg border border-border px-3 text-sm">{readerCopy.more}</button>}
              </section>}
              <p className="whitespace-pre-wrap border-t border-border pt-4 text-sm leading-relaxed text-foreground/85">{selected.body_text || selected.snippet || ""}</p>
              {selected.organization_snapshot && <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" aria-label={readerCopy.organize}>
                {EMAIL_OPERATIONS.map(operation => <button key={operation} type="button" onClick={() => reviewOrganization(operation)} className="min-h-11 rounded-xl border border-border px-3 text-sm font-medium">{readerCopy.operations[operation]}</button>)}
              </div>}
              {!!selected.attachments?.length && <section className="space-y-2" aria-label={readerCopy.attachments}>
                <h4 className="text-sm font-semibold">{readerCopy.attachments}</h4>
                {selected.attachments.map(attachment => <div key={attachment.id} className="rounded-xl border border-border p-3">
                  <p className="break-words text-sm">{attachment.filename}</p><p className="text-xs text-muted-foreground">{Math.round(attachment.bytes / 1024)} KB</p>
                  {emailAttachmentReadable(attachment) && <button type="button" disabled={attachment.bytes > 3 * 1024 * 1024} onClick={() => void readAttachment(attachment)} className="mt-2 min-h-11 rounded-lg border border-border px-3 text-sm">{readerCopy.read}</button>}
                  <button type="button" disabled={attachment.bytes > 3 * 1024 * 1024} onClick={() => void downloadAttachment(attachment)} className="ml-2 mt-2 min-h-11 rounded-lg border border-border px-3 text-sm">{readerCopy.download}</button>
                  {attachmentPreview?.id === attachment.id && <><pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-words text-sm">{attachmentPreview.text}</pre>{attachmentPreview.truncated && <p className="text-xs text-muted-foreground">{readerCopy.truncated}</p>}</>}
                </div>)}
              </section>}
              {selected.attachments_partial && <p className="text-xs text-muted-foreground">{readerCopy.partial}</p>}

            </div>
          )}

          {mode === "organize" && !selected && prefill.message_id && <button type="button" onClick={() => void openMessage({ id: prefill.message_id! })} disabled={busy} className="min-h-11 w-full rounded-xl border border-border px-3 text-sm font-medium">{t("mirror.email.open")}</button>}
          {mode === "organize" && selected && organizeReview && (
            <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
              <button type="button" onClick={() => { setMode("search"); setOrganizeReview(null); }} className="min-h-11 text-sm font-medium text-primary">{t("common.back")}</button>
              <h3 ref={readerHeadingRef} tabIndex={-1} className="break-words font-semibold">{selected.subject || t("mirror.email.noSubject")}</h3>
              <p className="break-words text-xs text-muted-foreground">{selected.from_name || selected.from || ""}</p>
              <p className="font-medium">{readerCopy.operations[organizeReview.operation]}</p><p className="text-sm text-muted-foreground">{readerCopy.review}</p>
            </div>
          )}

          {(mode === "draft" || mode === "send") && (
            <div className="space-y-3">
              <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">{t("mirror.email.to")}</span><input value={to} onChange={(e) => setTo(e.target.value)} className="min-h-11 w-full rounded-xl border border-border bg-background px-3 text-sm" /></label>
              <details className="rounded-xl border border-border px-3 py-2"><summary className="cursor-pointer text-xs font-medium text-muted-foreground">CC / BCC</summary><div className="mt-3 space-y-2"><input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="CC" className="min-h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" /><input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="BCC" className="min-h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" /></div></details>
              <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">{t("mirror.email.subject")}</span><input value={subject} onChange={(e) => setSubject(e.target.value)} className="min-h-11 w-full rounded-xl border border-border bg-background px-3 text-sm" /></label>
              <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">{t("mirror.email.body")}</span><textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={8} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm leading-relaxed" /></label>
              <div className="space-y-2">
                <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-border bg-background px-3 text-sm font-medium text-foreground">
                  <FilePlus2 className="h-4 w-4" />{t("mirror.email.addAttachment")}
                  <input type="file" multiple className="sr-only" onChange={(e) => { void addAttachments(e.target.files); e.currentTarget.value = ""; }} />
                </label>
                {attachments.length > 0 && <div className="space-y-1">{attachments.map((attachment, index) => <div key={`${attachment.filename}-${index}`} className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs"><span className="min-w-0 flex-1 truncate">{attachment.filename}</span><span className="text-muted-foreground">{Math.max(1, Math.round(attachment.bytes / 1024))} KB</span><button type="button" aria-label={t("mirror.email.removeAttachment")} onClick={() => setAttachments((items) => items.filter((_, i) => i !== index))} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button></div>)}</div>}
                <p className="text-[11px] text-muted-foreground">{t("mirror.email.attachmentHint")}</p>
              </div>
            </div>
          )}

          {mode === "reply" && selected && (
            <div className="space-y-3">
              <button type="button" onClick={() => setSelected(null)} className="inline-flex min-h-10 items-center gap-1 text-sm font-medium text-primary"><ChevronLeft className="h-4 w-4" />{t("mirror.email.chooseAnother")}</button>
              <div className="min-w-0 rounded-xl border border-border bg-muted/30 p-3 [overflow-wrap:anywhere]"><p className="text-sm font-semibold text-foreground">{selected.subject || t("mirror.email.noSubject")}</p><p className="mt-1 text-xs text-muted-foreground">{selected.from_name || selected.from || ""}</p></div>
              <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">{t("mirror.email.replyBody")}</span><textarea ref={replyInputRef} value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={8} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm leading-relaxed" /></label>
              <div className="space-y-2">
                <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-border bg-background px-3 text-sm font-medium text-foreground">
                  <FilePlus2 className="h-4 w-4" />{t("mirror.email.addAttachment")}
                  <input type="file" multiple className="sr-only" onChange={(e) => { void addAttachments(e.target.files); e.currentTarget.value = ""; }} />
                </label>
                {attachments.length > 0 && <div className="space-y-1">{attachments.map((attachment, index) => <div key={`${attachment.filename}-${index}`} className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs"><span className="min-w-0 flex-1 truncate">{attachment.filename}</span><span className="text-muted-foreground">{Math.max(1, Math.round(attachment.bytes / 1024))} KB</span><button type="button" aria-label={t("mirror.email.removeAttachment")} onClick={() => setAttachments((items) => items.filter((_, i) => i !== index))} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button></div>)}</div>}
              </div>
            </div>
          )}

          </fieldset>
          {visibleError && <p role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">{visibleError}</p>}

          {(mode === "draft" || mode === "send" || (mode === "reply" && selected) || (mode === "organize" && selected && organizeReview)) && (
            <button type="button" onClick={() => void executeWrite()} disabled={busy || !write.ready} className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "send" || mode === "reply" ? <Send className="h-4 w-4" /> : <Mail className="h-4 w-4" />}{writeUncertain ? t("mirror.connector.checkResult") : mode === "organize" ? readerCopy.confirm : t(`mirror.email.${mode}.confirm`)}</button>
          )}
        </div>
      )}
    </ConnectionManageSurface>
  );
}
